import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import { request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/lux-loyalty`;

type ListInfiniteApplicationsOperation = paths['/api/lux-loyalty/infinite/applications']['get'];
type GetInfiniteApplicationOperation = paths['/api/lux-loyalty/infinite/applications/{id}']['get'];
type ActionInfiniteApplicationOperation = paths['/api/lux-loyalty/infinite/applications/{id}/actions']['post'];
type ImportInfiniteEligibilitiesOperation = paths['/api/lux-loyalty/infinite/eligibilities']['post'];
type ListInfiniteEligibilitiesOperation = paths['/api/lux-loyalty/infinite/eligibilities']['get'];
type UpdateInfiniteEligibilityOperation = paths['/api/lux-loyalty/infinite/eligibilities/{id}']['patch'];

export type ListInfiniteApplicationsResponse =
  ListInfiniteApplicationsOperation['responses']['200']['content']['application/json'];
export type InfiniteApplicationSummary = ListInfiniteApplicationsResponse['result']['applications'][number];
export type InfiniteApplicationApplicant = NonNullable<InfiniteApplicationSummary['primaryApplicant']>;
export type InfiniteApplicationStatus = InfiniteApplicationSummary['status'];
export type InfiniteApplicantRole = InfiniteApplicationApplicant['role'];
export type InfinitePersonTitle = NonNullable<InfiniteApplicationApplicant['title']>;
export type InfiniteApplicantRelationship = NonNullable<InfiniteApplicationApplicant['relationship']>;

export type GetInfiniteApplicationResponse =
  GetInfiniteApplicationOperation['responses']['200']['content']['application/json'];
export type InfiniteApplication = GetInfiniteApplicationResponse['result'];
export type InfiniteApplicationBookingAuthority = InfiniteApplication['bookingAuthorities'][number];
export type InfiniteApplicationAssociatedEligibility = NonNullable<InfiniteApplication['associatedEligibility']>;

export type InfiniteApplicationActionBody =
  ActionInfiniteApplicationOperation['requestBody']['content']['application/json'];
export type InfiniteApplicationActionResponse =
  ActionInfiniteApplicationOperation['responses']['200']['content']['application/json'];

export type InfiniteEligibleUserInput =
  ImportInfiniteEligibilitiesOperation['requestBody']['content']['application/json']['eligibleUsers'][number];
export type AddInfiniteEligibleUsersResponse =
  ImportInfiniteEligibilitiesOperation['responses']['201']['content']['application/json'];
export type ListInfiniteEligibilitiesResponse =
  ListInfiniteEligibilitiesOperation['responses']['200']['content']['application/json'];
export type InfiniteEligibility = ListInfiniteEligibilitiesResponse['result']['eligibilities'][number];
export type UpdateInfiniteEligibilityInput =
  UpdateInfiniteEligibilityOperation['requestBody']['content']['application/json'];
export type UpdateInfiniteEligibilityResponse =
  UpdateInfiniteEligibilityOperation['responses']['200']['content']['application/json'];

interface ListInfiniteApplicationsParams {
  page?: number;
  pageSize?: number;
  status?: InfiniteApplicationStatus;
  email?: string;
}

interface ListInfiniteEligibilitiesParams {
  page?: number;
  pageSize?: number;
  email?: string;
  isRetracted?: boolean;
}

export async function listInfiniteApplications(
  params: ListInfiniteApplicationsParams = {},
): Promise<ListInfiniteApplicationsResponse> {
  const query = new URLSearchParams();

  if (params.page !== undefined) {
    query.set('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    query.set('pageSize', String(params.pageSize));
  }
  if (params.status) {
    query.set('status', params.status);
  }
  if (params.email) {
    query.set('email', params.email);
  }

  const qs = query.toString();
  return request(`${BASE_PATH}/infinite/applications${qs ? `?${qs}` : ''}`, { method: 'GET' });
}

export async function getInfiniteApplication(id: string): Promise<GetInfiniteApplicationResponse> {
  return request(`${BASE_PATH}/infinite/applications/${encodeURIComponent(id)}`, { method: 'GET' });
}

export async function actionInfiniteApplication(
  id: string,
  body: InfiniteApplicationActionBody,
): Promise<InfiniteApplicationActionResponse> {
  return request(`${BASE_PATH}/infinite/applications/${encodeURIComponent(id)}/actions`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function addInfiniteEligibleUsers(
  eligibleUsers: Array<InfiniteEligibleUserInput>,
): Promise<AddInfiniteEligibleUsersResponse> {
  return request(`${BASE_PATH}/infinite/eligibilities`, {
    method: 'POST',
    body: JSON.stringify({ eligibleUsers }),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function listInfiniteEligibilities(
  params: ListInfiniteEligibilitiesParams = {},
): Promise<ListInfiniteEligibilitiesResponse> {
  const query = new URLSearchParams();

  if (params.page !== undefined) {
    query.set('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    query.set('pageSize', String(params.pageSize));
  }
  if (params.email) {
    query.set('email', params.email);
  }
  if (params.isRetracted !== undefined) {
    query.set('isRetracted', String(params.isRetracted));
  }

  const qs = query.toString();
  return request(`${BASE_PATH}/infinite/eligibilities${qs ? `?${qs}` : ''}`, { method: 'GET' });
}

export async function updateInfiniteEligibility(
  id: string,
  body: UpdateInfiniteEligibilityInput,
): Promise<UpdateInfiniteEligibilityResponse> {
  return request(`${BASE_PATH}/infinite/eligibilities/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}
